// Les lignes de comparaison multi-échelles d'une tuile — LA source unique.
//
// Extrait de `ComparaisonKpi` (components.tsx) le 22/09, sans changer un pixel du rendu.
// Motif : le partage en image redessine la tuile à partir des données, et il en avait
// écrit SA propre version — `fmt(e.valeur, unite_de_la_tuile)` sur chaque ligne. Trois
// divergences, toutes silencieuses :
//
//  1. un STOCK affiche à l'écran la PART de la commune dans le périmètre (« 65,9 % »),
//     jamais le total du périmètre — « comparer 52 724 ménages à 30 millions ne se lit
//     pas ». L'image envoyait les totaux ;
//  2. l'unité de LIGNE prime sur celle de la tuile (l'écart en points sous un taux en %).
//     L'image collait l'unité de la tuile partout ;
//  3. une ligne d'ÉCART se rend signée (`fmtDelta` : « +2,9 », « ±0 »). L'image l'écrivait
//     nue, donc sans dire de quel côté on se trouve.
//
// Un maire qui partage « Ménages » par WhatsApp aurait envoyé des chiffres qui ne sont pas
// ceux qu'il a sous les yeux. C'est exactement ce que ce lot promettait d'éviter, et le
// seul remède durable n'est pas de recopier la règle au bon endroit — c'est qu'il n'y ait
// qu'UN endroit. L'écran et l'image appellent désormais cette fonction ; le JSX de
// `ComparaisonKpi` ne fait plus que poser les classes autour de ce qu'elle rend.
import { fmt, fmtDelta, uniteComparaison } from './format'
import type { EchelleKpi } from './types'

/** Une ligne prête à rendre : l'écran en fait du JSX, l'image du texte. */
export interface LigneComparaison {
  /** Clé de rendu (`e.echelle`). */
  cle: string
  /** Nom réel du périmètre (« Essonne »). */
  libelle: string
  /** Rôle générique (« Son département »), en infobulle — absent s'il redit le libellé. */
  role?: string
  /** Valeur du producteur lui-même, pas notre agrégat. */
  publiee: boolean
  /** Nom du producteur, pour l'infobulle du marqueur « publié ». */
  producteur?: string
  /** Couverture, écrite SEULEMENT lorsqu'elle n'est pas totale. */
  couverturePartielle?: number
  /** Mention propre à cette échelle (24/08). */
  mentionLigne?: string
  /** Le nombre, déjà formaté — « 65,9 % », « +2,9 », « 1 338 485 » ou « — ». */
  valeur: string
  /** L'unité à écrire après le nombre. Absente sur une part (le « % » est dans la valeur). */
  unite?: string
}

export interface Comparaison {
  lignes: LigneComparaison[]
  /** Vrai = les lignes portent des PARTS, pas des niveaux. Change le pied de bloc. */
  estPart: boolean
  /** Pied du bloc : la mention de part, ou les notes de méthode dédoublonnées. */
  notes: string[]
  /** Réserves : couverture non mesurée, échelles non comparables. */
  reserve?: string
}

// Les phrases de MÉTHODE que le backend répétait dans chaque `note_methode` et que la note
// de page porte désormais une seule fois (A2). Retirées de la tuile, jamais du produit :
// ce qui reste — « Source : X, millésime Y » — est propre à l'indicateur et reste collé à
// sa valeur. Retrait par phrase connue, jamais par découpe aveugle : une note qu'on ne
// reconnaît pas est rendue telle quelle plutôt que mutilée.
// ⚠️ FILET TRANSITOIRE : la moitié backend d'A2 est faite, ces phrases ne sont plus
// composées. Le retrait au rendu ne couvre que la fenêtre de cache (`_cache_comp`, 6 h).
const GENERIQUES = new RegExp([
  "agrégat calculé et publié par l'INSEE pour ce périmètre\\.",
  "moyenne de strate calculée et publiée par la DGCL \\(le référentiel officiel qui régit "
  + "les dotations\\) — aucune reconstitution de notre part",
  "valeur publiée par le SSMSI, non masquée à cette échelle",
  "somme des départements métropolitains — Σfaits / Σdénominateur du producteur ; "
  + "croisée contre la somme des régions, écart nul",
  "non masquée à cette échelle",
  "Aucune reconstitution de notre part\\.?",
].join('|'), 'g')

const NOMS_PRODUCTEUR: Record<string, string> = {
  dgcl_publie: 'la DGCL', ssmsi_publie: 'le SSMSI', insee_publie: "l'INSEE",
}

/* Trois origines publiées : ancres INSEE, moyennes de strate DGCL (F2, 07/08) et bases
   départementale / régionale du SSMSI (09/08). Dire « calculée par nos soins » y serait
   faux, et faux dans le sens qui nous dessert : on donnerait pour un bricolage maison un
   chiffre officiel du producteur. La ligne France de la sécurité, elle, est bien la
   NÔTRE (somme des départements métropolitains) et reste dans les calculées. */
const estPubliee = (e: EchelleKpi) =>
  e.origine === 'insee_publie' || e.origine === 'dgcl_publie' || e.origine === 'ssmsi_publie'
  // Socle (26/09) : la valeur publiée par le producteur de l'indicateur, quel qu'il soit —
  // son nom vient avec la ligne (`e.producteur`).
  || e.origine === 'producteur_publie'

/**
 * Construit les lignes de comparaison et le pied du bloc. `null` quand aucune échelle
 * n'est servie — l'appelant ne rend alors rien du tout.
 */
export function construireComparaison(echelles: EchelleKpi[],
                                      uniteKpi?: string): Comparaison | null {
  const unite = uniteComparaison(uniteKpi)
  const servies = echelles.filter(e => e.valeur !== null)
  if (!servies.length) return null

  // Un STOCK affiche la PART de la commune (« je pèse 65,9 % des ménages de mon interco »),
  // jamais le total du périmètre : comparer 52 724 ménages à 30 millions ne se lit pas.
  const estPart = servies.some(e => e.part_commune_pct != null)

  // TOUTES les notes distinctes, pas seulement la première (revue croisée 02/08) : une
  // comparaison peut être MIXTE — l'intercommunalité sur notre agrégat pendant que
  // département, région et France portent des ancres INSEE. En ne gardant que la première,
  // la source et le millésime du fichier officiel n'apparaissaient jamais.
  const notes = [...new Set(servies.map(e => e.note_methode).filter(Boolean))] as string[]
  const notesPropres = notes
    .map(n => n.replace(GENERIQUES, '').replace(/\s{2,}/g, ' ').trim())
    .map(n => n.replace(/^[—–-]\s*/, '').replace(/^\w/, c => c.toUpperCase()))
    .filter(n => n.length > 0)

  const lignes: LigneComparaison[] = servies.map(e => {
    const couv = e.couverture_communes_pct
    const partielle = couv != null && couv < 100
    let valeur: string
    let uniteLigne: string | undefined
    if (estPart) {
      valeur = e.part_commune_pct != null
        ? `${e.part_commune_pct.toLocaleString('fr-FR')} %` : '—'
      uniteLigne = undefined
    } else {
      // L'unité de LIGNE prime sur celle de la tuile (revue codex 08/08, fusion SRU) : une
      // comparaison peut porter une autre grandeur que la valeur principale — l'écart en
      // points sous un taux en %. A12 : `uniteComparaison` s'applique AUSSI à l'unité de
      // ligne, sinon la règle d'abrègement saute exactement là où elle sert.
      const u = uniteComparaison(e.unite) ?? unite
      // C2 : une ligne d'ÉCART se rend signée des deux côtés — « +2,9 » comme « −4,2 », et
      // « ±0 » quand l'arrondi efface l'écart plutôt qu'un « +0 » faussement exact.
      valeur = e.signe ? fmtDelta(e.valeur as number, u) : fmt(e.valeur as number, u)
      uniteLigne = u
    }
    return {
      cle: e.echelle,
      libelle: e.libelle,
      role: e.role && e.role !== e.libelle ? e.role : undefined,
      publiee: estPubliee(e),
      producteur: e.producteur || NOMS_PRODUCTEUR[e.origine ?? ''] || "l'INSEE",
      couverturePartielle: partielle ? (couv as number) : undefined,
      mentionLigne: e.mention_ligne ?? undefined,
      valeur,
      unite: uniteLigne,
    }
  })

  // Une échelle SANS AUCUNE commune renseignée est écartée de `servies` et disparaît. Sans
  // la nommer, le pied dirait « tous les périmètres sont renseignés en totalité » — vrai
  // pour ceux qui restent, trompeur pour le lecteur, qui ne peut pas deviner qu'une échelle
  // a été retirée. Une comparaison manquante est une information, pas un blanc à combler.
  const calculees = servies.filter(e => !estPubliee(e))
  const couvInconnue = calculees.filter(e => e.couverture_communes_pct == null)
  const absentes = echelles.filter(e => e.valeur === null)
  const liste = (xs: EchelleKpi[]) => xs.map(e => e.libelle).join(' · ')
  // T11 (CDC « témoins partout », § 5 bis, 17/10) : la RAISON d'une échelle absente vient
  // du serveur (`raison_nd`) et se dit telle quelle. « Faute de donnée » était faux pour la
  // sécurité, où la donnée existe et où c'est le producteur qui ne publie pas l'échelle ;
  // cette phrase générique ne reste que pour les échelles servies sans raison.
  const motif = (e: EchelleKpi) => e.raison_nd?.trim().replace(/[\s.]+$/, '') || ''
  const expliquees = absentes.filter(e => motif(e))
  const muettes = absentes.filter(e => !motif(e))
  const reserve = [
    couvInconnue.length > 0 ? `Couverture non mesurée pour : ${liste(couvInconnue)}.` : '',
    ...expliquees.map(e => `Pas de comparaison avec ${e.libelle} : ${motif(e)}.`),
    muettes.length > 0
      ? `Non comparable, faute de donnée sur le périmètre : ${liste(muettes)}.` : '',
  ].filter(Boolean).join(' ')

  return {
    lignes,
    estPart,
    notes: estPart ? ['part de la commune dans chaque périmètre'] : notesPropres,
    reserve: reserve || undefined,
  }
}

/** Le côté droit d'une ligne, en texte — pour l'image, qui n'a pas de balises pour séparer
 *  le nombre de son unité. La règle de concaténation vit ici, avec le reste. */
export const texteValeur = (l: LigneComparaison): string =>
  l.unite ? `${l.valeur} ${l.unite}` : l.valeur

/** Le côté gauche d'une ligne, en texte : le périmètre et ce qui le qualifie. La couverture
 *  partielle et le marqueur « publié » restent à l'écran — sur une image envoyée par
 *  messagerie, quatre lignes déjà denses n'absorbent pas une quatrième mention. */
export const texteLibelle = (l: LigneComparaison): string =>
  l.mentionLigne ? `${l.libelle} · ${l.mentionLigne}` : l.libelle
